/**
 * Number, money, percent, date and age formatting. Every screen uses these so the same value
 * reads the same everywhere. A missing value is always an en dash, never 0.
 */

export const DASH = '–';

type Num = number | null | undefined;
const isNum = (v: Num): v is number => typeof v === 'number' && Number.isFinite(v);

const nf = new Map<string, Intl.NumberFormat>();
function intl(opts: Intl.NumberFormatOptions) {
  const key = JSON.stringify(opts);
  let f = nf.get(key);
  if (!f) {
    f = new Intl.NumberFormat('en-US', opts);
    nf.set(key, f);
  }
  return f;
}

/** 1234 → "1,234". `compact` → "12.9K" from 1,000 up. `digits` = max fraction digits (default 0). */
export function fmtNum(v: Num, opts: { compact?: boolean; digits?: number } = {}): string {
  if (!isNum(v)) return DASH;
  const digits = opts.digits ?? 0;
  if (opts.compact && Math.abs(v) >= 1000) {
    return intl({ notation: 'compact', maximumFractionDigits: 1 }).format(v);
  }
  return intl({ maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(v);
}

/**
 * Money. Two decimals under $10, whole dollars from $10 up, and up to four significant
 * decimals under one cent so provider costs ($0.0006) stay readable. `exact` always gives cents.
 */
export function fmtUsd(v: Num, opts: { exact?: boolean } = {}): string {
  if (!isNum(v)) return DASH;
  const abs = Math.abs(v);
  if (opts.exact) return intl({ style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  if (abs > 0 && abs < 0.01) {
    return intl({ style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumSignificantDigits: 2 }).format(v);
  }
  if (abs < 10) return intl({ style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  return intl({ style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
}

/**
 * Percent. Input is a 0–1 ratio by default (rates in `ai_scores`); pass `from: 'percent'` for
 * values already on a 0–100 scale (`visibility_pct`, scores). One decimal under 10%, else whole,
 * unless `digits` is given.
 */
export function fmtPct(v: Num, opts: { from?: 'ratio' | 'percent'; digits?: number } = {}): string {
  if (!isNum(v)) return DASH;
  const pct = opts.from === 'percent' ? v : v * 100;
  const digits = opts.digits ?? (Math.abs(pct) > 0 && Math.abs(pct) < 10 ? 1 : 0);
  return `${intl({ maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(pct)}%`;
}

export type Tone = 'good' | 'bad' | 'flat';
export type Delta = { text: string; tone: Tone };

/**
 * A signed change. Positive is good unless `higherIsBetter: false` (spend, errors). A change that
 * rounds to zero is flat. Note: for rank positions use `delta_*` from `v_tracked_latest`, which is
 * already "positive = improved".
 */
export function fmtDelta(
  v: Num,
  opts: { higherIsBetter?: boolean; digits?: number; unit?: string; format?: (n: number) => string } = {},
): Delta {
  if (!isNum(v)) return { text: DASH, tone: 'flat' };
  const digits = opts.digits ?? 0;
  const factor = 10 ** digits;
  const rounded = Math.round(v * factor) / factor;
  const unit = opts.unit ?? '';
  const body = (n: number) => (opts.format ? opts.format(n) : n.toFixed(digits));
  if (rounded === 0) return { text: `${body(0)}${unit}`, tone: 'flat' };
  const up = rounded > 0;
  const good = opts.higherIsBetter === false ? !up : up;
  const text = `${up ? '+' : '-'}${body(Math.abs(rounded))}${unit}`;
  return { text, tone: good ? 'good' : 'bad' };
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Parses an ISO timestamp, or a `date` column ("2026-10-15") as that calendar day. */
export function parseDate(v: string | number | Date | null | undefined): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string' && DATE_ONLY.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * "Oct 15, 2026". Date-only values are shown as written; timestamps in UTC by default, since
 * every schedule in the tool runs in UTC. `time: true` adds "3:05 PM UTC".
 */
export function fmtDate(
  v: string | number | Date | null | undefined,
  opts: { time?: boolean; year?: boolean; timeZone?: string } = {},
): string {
  const d = parseDate(v);
  if (!d) return DASH;
  const tz = opts.timeZone ?? 'UTC';
  const base: Intl.DateTimeFormatOptions = { timeZone: tz, month: 'short', day: 'numeric' };
  if (opts.year !== false) base.year = 'numeric';
  const day = d.toLocaleDateString('en-US', base);
  if (!opts.time || (typeof v === 'string' && DATE_ONLY.test(v))) return day;
  const t = d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  return `${day}, ${t}${tz === 'UTC' ? ' UTC' : ''}`;
}

/** "5 min ago", "3 h ago" (under 48 h), "2 days ago". Same steps as the reference page. */
export function ago(v: string | number | Date | null | undefined, now: number = Date.now()): string {
  const d = parseDate(v);
  if (!d) return 'never';
  const mins = Math.round((now - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/** Hours since a time, or Infinity when there is none. */
export function ageHours(v: string | number | Date | null | undefined, now: number = Date.now()): number {
  const d = parseDate(v);
  return d ? (now - d.getTime()) / 3_600_000 : Number.POSITIVE_INFINITY;
}

/** "rank_collect" → "Rank collect". For job kinds and other snake_case keys. */
export function humanize(key: string | null | undefined): string {
  if (!key) return DASH;
  const s = key.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
